import type {
  DocumentNode,
  OperationVariables,
  PreloadedQueryRef,
  PreloadQueryFetchPolicy,
  PreloadQueryOptions,
  TypedDocumentNode,
} from '@apollo/client';
import { canonicalStringify, getOperationName } from '@apollo/client/utilities';
import { activeClerkScope, preloadQuery } from 'src/lib/apolloClient';
import type { AppRouterContext } from './routerAccess';

/** How long a settled preload nobody has read yet still serves a navigation (hover, then click). */
export const ROUTE_PRELOAD_REUSE_MS = 5_000;

export type RoutePreloadOptions = Readonly<{ fetchPolicy: PreloadQueryFetchPolicy }>;

type RoutePreloadEntry = {
  readonly key: string;
  readonly ref: object;
  readonly document: DocumentNode;
  readonly variablesKey: string;
  status: 'pending' | 'ready' | 'failed';
  settledAt: number;
  readers: number;
  read: boolean;
};

/** `preloadQuery` narrowed to the one call shape loaders use: typed variables plus a fetch policy. */
const preloadWithVariables: <TData, TVariables extends OperationVariables>(
  query: TypedDocumentNode<TData, TVariables>,
  options: Omit<PreloadQueryOptions, 'variables'> & { variables: TVariables; fetchPolicy: PreloadQueryFetchPolicy }
) => PreloadedQueryRef<TData, TVariables> = preloadQuery;

const entries = new Map<string, RoutePreloadEntry>();
const entryByRef = new WeakMap<object, RoutePreloadEntry>();

function reusable(entry: RoutePreloadEntry, now: number): boolean {
  if (entry.status === 'failed') return false;
  if (entry.status === 'pending' || entry.readers > 0) return true;
  return !entry.read && now - entry.settledAt < ROUTE_PRELOAD_REUSE_MS;
}

function prune(now: number): void {
  for (const [key, entry] of entries) {
    if (!reusable(entry, now)) entries.delete(key);
  }
}

/** True when a route loader started `value` (a retry then re-runs the loader rather than refetching it). */
export function isRoutePreloadRef(value: object): boolean {
  return entryByRef.has(value);
}

/** True when `value` is a ref this registry started for exactly this document and these variables. */
export function isRouteQueryRefFor<TData, TVariables extends OperationVariables>(
  value: unknown,
  document: TypedDocumentNode<TData, TVariables>,
  variables: TVariables
): value is PreloadedQueryRef<TData, TVariables> {
  if (typeof value !== 'object' || value === null) return false;
  const entry = entryByRef.get(value);
  return entry?.document === document && entry.variablesKey === canonicalStringify(variables);
}

/**
 * Starts a page's first query from a route loader, or hands back the ref already started for the same
 * session, organization, operation, variables and fetch policy. Signed-out, loading or org-less sessions
 * get undefined, so the page loads the query itself.
 */
export function preloadRouteQuery<TData, TVariables extends OperationVariables>(
  context: AppRouterContext,
  document: TypedDocumentNode<TData, TVariables>,
  variables: TVariables,
  { fetchPolicy }: RoutePreloadOptions
): PreloadedQueryRef<TData, TVariables> | undefined {
  if (context.auth.loading || !context.auth.isAuthenticated) return undefined;
  const scope = activeClerkScope();
  if (scope === undefined) return undefined;

  const now = Date.now();
  prune(now);
  const variablesKey = canonicalStringify(variables);
  const key = [scope.sessionId, scope.organizationId, getOperationName(document) ?? '', variablesKey, fetchPolicy].join(
    '|'
  );
  const existing = entries.get(key)?.ref;
  if (isRouteQueryRefFor(existing, document, variables)) return existing;

  const ref = preloadWithVariables(document, { variables, fetchPolicy });
  const entry: RoutePreloadEntry = {
    key,
    ref,
    document,
    variablesKey,
    status: 'pending',
    settledAt: 0,
    readers: 0,
    read: false,
  };
  entries.set(key, entry);
  entryByRef.set(ref, entry);
  ref.toPromise().then(
    () => {
      entry.status = 'ready';
      entry.settledAt = Date.now();
    },
    () => {
      entry.status = 'failed';
      if (entries.get(key) === entry) entries.delete(key);
    }
  );
  return ref;
}

/**
 * Marks a preloaded ref as read by a mounted page. While read it is reused by every loader re-run;
 * once released it never serves another navigation, so the next visit asks the server again.
 */
export function retainRouteQueryRef(ref: object): () => void {
  const entry = entryByRef.get(ref);
  if (entry === undefined) return () => {};
  entry.readers += 1;
  entry.read = true;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    entry.readers -= 1;
  };
}

/** Test seam: forget every preload. */
export function clearRoutePreloads(): void {
  entries.clear();
}
